/**
 * MSAL / API configuration loaded from Vite env vars at build time.
 *
 * Fail-closed: missing required env vars throw at module load — the SPA won't
 * boot rather than running with broken auth. Per
 * `docs/development/development-process.md §1`.
 */
import type { Configuration, RedirectRequest } from '@azure/msal-browser';

function requireEnv(name: string): string {
    const value = import.meta.env[name];
    if (typeof value !== 'string' || value.trim() === '') {
        throw new Error(
            `Required Vite env var is missing: ${name}. Set it in .env.<mode> before building.`
        );
    }
    return value;
}

function optionalEnv(name: string): string | undefined {
    const value = import.meta.env[name];
    return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

const apiEndpoint = requireEnv('VITE_API_ENDPOINT').replace(/\/+$/, '');
const clientId = requireEnv('VITE_AUTH_CLIENT_ID');
const authority = requireEnv('VITE_AUTH_AUTHORITY');
const scopes = requireEnv('VITE_AUTH_SCOPES')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
const tenantDisplayName = optionalEnv('VITE_TENANT_DISPLAY_NAME') ?? 'docreview';

/**
 * Google Picker config — optional. When both API key and client ID are set,
 * the admin Add-source modal swaps the paste-folder-ID UX for a Google
 * Drive folder picker. Leave unset (or empty) to keep the legacy paste
 * path.
 *
 * The client ID is a *separate* Google OAuth client from the External ID
 * federation client — this one is purely browser-side and asks for the
 * `drive.readonly` scope at picker-launch time.
 */
const googlePickerApiKey = optionalEnv('VITE_GOOGLE_PICKER_API_KEY');
const googlePickerClientId = optionalEnv('VITE_GOOGLE_PICKER_CLIENT_ID');

// Surface a console warning when only ONE of the two Picker vars is set.
// Both are required for the picker to work; a half-configured build would
// silently fall back to the legacy paste-folder-ID UX, which is a subtle
// deploy bug that admins won't notice until they look for the button.
if (googlePickerApiKey && !googlePickerClientId) {
    // eslint-disable-next-line no-console
    console.warn(
        '[docreview] VITE_GOOGLE_PICKER_API_KEY is set but VITE_GOOGLE_PICKER_CLIENT_ID is not — Drive Picker is disabled. Set both to enable.'
    );
} else if (!googlePickerApiKey && googlePickerClientId) {
    // eslint-disable-next-line no-console
    console.warn(
        '[docreview] VITE_GOOGLE_PICKER_CLIENT_ID is set but VITE_GOOGLE_PICKER_API_KEY is not — Drive Picker is disabled. Set both to enable.'
    );
}

export const apiConfig = {
    apiEndpoint,
    tenantDisplayName,
    googlePicker:
        googlePickerApiKey && googlePickerClientId
            ? { apiKey: googlePickerApiKey, clientId: googlePickerClientId }
            : null
} as const;

export const msalConfig: Configuration = {
    auth: {
        clientId,
        authority,
        knownAuthorities: [new URL(authority).host],
        redirectUri: window.location.origin + '/',
        postLogoutRedirectUri: window.location.origin + '/'
    },
    cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false
    },
    system: {
        // Reduce token-acquisition log spam.
        loggerOptions: { logLevel: 0 }
    }
};

/**
 * Always go through the IdP picker on explicit sign-in / re-auth. Without
 * `prompt: 'select_account'`, External ID for customers will sometimes
 * route federated users (Google) to a local-account password prompt for
 * their synthetic `<objectId>@bacrexternalauth.onmicrosoft.com` UPN —
 * the federated user has no local password, so they're stuck unless they
 * find the small "Sign in with another account" link.
 *
 * select_account always lands on External ID's IdP-picker page, where
 * choosing Google triggers the federation handshake. With Google as the
 * only configured IdP, this is effectively one extra click on the cold
 * sign-in path and silent-token-renewal (`acquireTokenSilent`) is
 * unaffected (it doesn't consult `prompt`).
 */
export const loginRequest: RedirectRequest = {
    scopes,
    prompt: 'select_account'
};

export const apiRequest = {
    scopes
} as const;
